// not good import
import { brand } from 'config';
import { isGiftCardUpc, isEGiftCardUpc, isPGiftCardUpc } from '~/utils/GiftCardUtil';

const formatStatus = (product) => {
  const quantityRequested = Number(product.quantityRequested);
  const quantityPurchased = Number(product.quantityPurchased);
  const availabilityStatus = product.productInfo?.upc?.networkAvailability?.[0]?.available;
  const status = [];
  if (!Number.isNaN(Number(quantityPurchased)) && quantityPurchased >= 1) {
    status.push('Purchased');
  }
  if (!Number.isNaN(Number(quantityRequested)) && !Number.isNaN(Number(quantityPurchased)) && quantityPurchased < quantityRequested) {
    status.push('Still Needed');
  }
  if (!availabilityStatus) {
    status.push('Unavailable');
  }
  return status;
};

const containsPriceInfo = (product) => {
  const upc = product?.productInfo?.upc;
  const available = upc?.networkAvailability?.[0]?.available;
  const fulfillmentMethod = upc?.networkAvailability?.[0]?.fulfillmentMethod;
  const discontinuedCode = upc?.discontinuedCode?.toUpperCase() === 'Y';

  const isPurchased = product?.quantityRequested <= product?.quantityPurchased;

  if (isPurchased) {
    return true;
  }
  if (!available && discontinuedCode) {
    return true;
  }
  if (available && discontinuedCode) {
    return true;
  }
  if (upc?.bigTicketItemFlag) {
    return false;
  }
  if (!available && (!fulfillmentMethod?.includes('STH') || fulfillmentMethod?.length <= 0)) {
    return true;
  }
  if (
    !fulfillmentMethod?.includes('STH') &&
    (fulfillmentMethod?.includes('STOREONLY') || fulfillmentMethod?.includes('BOPS') || fulfillmentMethod?.includes('BOSS'))
  ) {
    return false;
  }
  return true;
};

const formatPrice = (price, product) => {
  if (!containsPriceInfo(product)) {
    return null;
  }

  const finalPrice = Number(price?.finalPrice);
  const retailPrice = Number(price?.retailPrice);
  const originalPrice = Number(price?.originalPrice);
  if (!Number.isNaN(finalPrice) && finalPrice !== 0) {
    return finalPrice;
  }
  if (!Number.isNaN(retailPrice) && retailPrice !== 0) {
    return retailPrice;
  }
  if (!Number.isNaN(originalPrice) && originalPrice !== 0) {
    return originalPrice;
  }
  return null;
};

const transformProducts = (products, type) => {
  if (!products) return [];
  let items = products.filter((product) => product?.productInfo);
  if (type === 'GIFT_GIVER') {
    items = items.filter(
      (product) =>
        !(!product?.productInfo?.upc?.networkAvailability?.[0]?.available && product?.productInfo?.upc?.discontinuedCode === 'Y') ||
        product?.quantityRequested <= product?.quantityPurchased,
    );
  }
  items = items.map((product, index) => {
    const { upc } = product?.productInfo ?? {};
    const price = product?.productInfo?.upc?.price?.[0];
    const transformedProduct = {
      id: product?.productInfo?.productId,
      keyId: product?.productInfo?.productId ? `item-${product?.productInfo?.productId}-${index}` : `item-0-${index}`,
      identifier: { productUrl: '' },
      category: Array.isArray(product?.productInfo?.filterCategories)
        ? product?.productInfo?.filterCategories?.map((name) => name?.toString()?.trim()?.toUpperCase())
        : [],
      price: formatPrice(price, product),
      status: formatStatus(product),
      categoryID: product?.productInfo?.defaultCategoryId,
      registryGUID: product?.registryGUID,
      vendorId: product?.productInfo?.vendorId,
      bigTicketItemFlag: upc?.bigTicketItemFlag ? upc?.bigTicketItemFlag : false,
      dreamFundFlag: upc?.upcName?.toUpperCase() === 'DREAM FUND',
      purchased: {
        sold: product?.quantityPurchased,
        quantity: product?.quantityRequested,
        maxQuantity: product?.productInfo?.maxPurchasableQuantity,
      },
      imagery: {
        primaryImage: {
          filePath: `${product?.productInfo?.imageDetails?.primaryPortraitSource}.tif`,
        },
      },
      detail: {
        name: upc?.upcName,
        description: upc?.upcModelDescription,
        upcId: upc?.upcId,
        brand: {
          name: upc?.brandName,
        },
        productUrl: product?.productInfo?.url?.productUrl,
        upcNumber: upc?.upcNumber,
      },
      traits: {
        colors: [upc?.colorDescription],
        size: [upc?.sizeDescription],
      },
      pricing: {
        price: {
          priceType: {},
          tieredPrice: [],
        },
        badges: [],
      },
      networkAvailability: [
        {
          available: upc?.networkAvailability?.[0]?.available,
          lowAvailable: upc?.networkAvailability?.[0]?.lowAvailable,
          fulfillmentChannel: upc?.networkAvailability?.[0]?.fulfillmentChannel,
          fulfillmentMethod: upc?.networkAvailability?.[0]?.fulfillmentMethod ? upc.networkAvailability[0]?.fulfillmentMethod : [],
          discontinuedCode: upc?.discontinuedCode ? upc.discontinuedCode : '',
        },
      ],
      isGiftCard: isGiftCardUpc(upc?.upcNumber),
      isEGiftCard: isEGiftCardUpc(upc?.upcNumber),
      isPGiftCard: isPGiftCardUpc(upc?.upcNumber),
      egiftCardId: product?.egiftCardId ? product?.egiftCardId : '',
    };

    // Price
    if (product?.productInfo?.upc?.price?.length > 0) {
      if (price?.priceTypeText) {
        transformedProduct.pricing.price.priceType.text = price?.priceTypeText;
      }

      // Sale Value - bracket value
      if (price?.saleValue !== '0.00' && +price?.originalPrice < +price?.saleValue) {
        const saleValue = {
          label: 'A [PRICE] value',
          values: [
            {
              value: price?.saleValue,
              formattedValue: `$${price?.saleValue}`,
              type: 'discount',
            },
          ],
        };
        transformedProduct.pricing.price.tieredPrice.push(saleValue);
      }

      // Original Price - strikethrough value
      if (price?.originalPrice) {
        const originalPrice = {
          label: '[PRICE]',
          values: [
            {
              value: price?.originalPrice,
              formattedValue: `$${price?.originalPrice}`,
              type: 'regular',
            },
          ],
        };
        transformedProduct.pricing.price.tieredPrice.push(originalPrice);
      }
      // Retail Price
      if (price?.retailPrice !== '0.00' && +price?.originalPrice > +price?.retailPrice) {
        const SaleLabel = price?.retailPriceLabel || 'Sale';
        const retailPrice = {
          label: `${SaleLabel} [PRICE]`,
          values: [
            {
              value: price.retailPrice,
              formattedValue: `$${price.retailPrice}`,
              type: 'discount',
            },
          ],
        };
        transformedProduct.pricing.price.tieredPrice.push(retailPrice);
      }

      // Promotion MCOM
      if (brand.toUpperCase() === 'MCOM' && price?.promotions?.[0]?.badgeText) {
        const promotion = {
          promoId: price?.promotions[0]?.promotionId,
          header: price?.promotions[0]?.badgeText,
        };
        transformedProduct.pricing.badges.push(promotion);
      }
      // Promotion BCOM
      if (brand.toUpperCase() === 'BCOM' && price?.promotions?.[0]?.offerDescription) {
        const promotion = {
          description: price?.promotions[0]?.offerDescription,
        };
        transformedProduct.pricing.badges.push(promotion);
      }

      // Final Price
      if (price?.finalPrice) {
        const finalPrice = {
          secondBadgeWithFinalPrice: true,
          finalPrice: {
            label: 'With offer [PRICE]',
            values: [
              {
                value: price?.finalPrice,
                formattedValue: `$${price?.finalPrice}`,
                type: 'discount',
              },
            ],
          },
          type: 'discount',
        };
        transformedProduct.pricing.badges.push(finalPrice);
      }
    }
    return transformedProduct;
  });

  const eGiftCardItem = items.find((obj) => obj.isEGiftCard);
  const pGiftCardItem = items.find((obj) => obj.isPGiftCard);
  if (eGiftCardItem && pGiftCardItem) {
    pGiftCardItem.eUpcNumber = eGiftCardItem.detail.upcNumber;
    pGiftCardItem.eUpcId = eGiftCardItem.detail.upcId;
    pGiftCardItem.egiftCardId = eGiftCardItem.egiftCardId;
    return [pGiftCardItem, ...items.filter((obj) => !obj.isGiftCard)];
  }
  return [...items.filter((obj) => obj.isGiftCard), ...items.filter((obj) => !obj.isGiftCard)];
};

export { formatStatus, formatPrice, transformProducts, containsPriceInfo };
